import React from 'react';
import styled, { css } from 'styled-components';
import { rgba, darken, lighten } from 'polished';
import { primaryColor, accessibleButtonFill } from '../../../utils/helpers';
import { palette } from '../../design-system/colors';
import { PropTypes } from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import defaultValidatorFn from '@emotion/is-prop-valid';

const StyledThemedButton = styled.button.withConfig({
    shouldForwardProp: (prop, elementToBeCreated) =>
      defaultValidatorFn(prop)
})`
    border: none;
    border-radius: 0.25rem;
    padding: 0.7rem 1.4rem;
    font-size: 14px;
    font-weight: 800;
    text-transform: uppercase;
    transition: ease 0.2s all;
    cursor: pointer;
    &:focus {
        box-shadow: 0 0 0 3px ${(props) => rgba(primaryColor(props), 0.25)};
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${(props) => accessibleButtonFill(primaryColor(props))};
        outline-offset: 2px;
    }

    ${(props) => {
        switch (props.mode) {
            case 'primary':
                return css`
                    color: white;
                    background-color: ${(props) =>
                        accessibleButtonFill(primaryColor(props))};
                    transition: ease 0.2s all;
                    &:hover {
                        background-color: ${(props) =>
                            darken(
                                0.1,
                                accessibleButtonFill(primaryColor(props))
                            )} !important;
                        border-color: ${(props) =>
                            darken(
                                0.05,
                                accessibleButtonFill(primaryColor(props))
                            )} !important;
                        color: white;
                    }

                    &:active {
                        background-color: ${(props) =>
                            darken(
                                0.2,
                                accessibleButtonFill(primaryColor(props))
                            )} !important;
                        border-color: ${(props) =>
                            darken(
                                0.15,
                                accessibleButtonFill(primaryColor(props))
                            )} !important;
                    }

                    &:focus {
                        box-shadow: 0 0 0 0.2rem
                            ${(props) => lighten(0.05, primaryColor(props))} !important;
                        background-color: ${(props) =>
                            darken(
                                0.1,
                                accessibleButtonFill(primaryColor(props))
                            )};
                    }
                `;
            case 'secondary':
                return css`
                    color: ${(props) => primaryColor(props)} !important;
                    background-color: ${(props) =>
                        rgba(primaryColor(props), 0.1)};
                    transition: ease 0.2s all;
                    &:hover {
                        background-color: ${(props) =>
                            rgba(primaryColor(props), 0.2)};
                    }
                    &:focus {
                        box-shadow: 0 0 0 0.2rem
                            ${(props) => rgba(primaryColor(props), 0.1)} !important;
                        background-color: ${(props) =>
                            rgba(primaryColor(props), 0.2)};
                    }
                `;
            case 'black':
                return css`
                    color: white;
                    background-color: #1d1e1d;
                    transition: ease 0.2s all;
                    &:hover {
                        background-color: black !important;
                        border-color: ${darken(0.05, '#1D1E1D')} !important;
                        color: white;
                    }

                    &:active {
                        background-color: black !important;
                        border-color: ${darken(0.15, '#1D1E1D')} !important;
                        color: white;
                    }

                    &:focus {
                        box-shadow: 0 0 0 0.2rem ${lighten(0.05, '#1D1E1D')} !important;
                        background-color: black;
                        color: white;
                    }
                `;
            case 'gray':
                return css`
                    color: black;
                    background-color: rgba(${palette.graphite.heavyMetal}, 0.6);
                    transition: ease 0.2s all;
                    &:hover {
                        background-color: ${darken(
                            0.1,
                            rgba(palette.graphite.heavyMetal, 0.6)
                        )} !important;
                        border-color: ${darken(
                            0.05,
                            rgba(palette.graphite.heavyMetal, 0.6)
                        )} !important;
                        color: white;
                    }

                    &:active {
                        background-color: ${darken(
                            0.2,
                            rgba(palette.graphite.heavyMetal, 0.6)
                        )} !important;
                        border-color: ${darken(
                            0.15,
                            rgba(palette.graphite.heavyMetal, 0.6)
                        )} !important;
                        color: white;
                    }

                    &:focus {
                        box-shadow: 0 0 0 0.2rem
                            ${lighten(
                                0.05,
                                rgba(palette.graphite.heavyMetal, 0.6)
                            )} !important;
                        background-color: ${darken(
                            0.1,
                            rgba(palette.graphite.heavyMetal, 0.6)
                        )};
                        color: white;
                    }
                `;
            case 'red':
                return css`
                    color: white;
                    background-color: ${palette.vibrant.raspberry};
                    transition: ease 0.2s all;
                    &:hover {
                        background-color: ${darken(
                            0.1,
                            palette.vibrant.raspberry
                        )} !important;
                        border-color: ${darken(
                            0.05,
                            palette.vibrant.raspberry
                        )} !important;
                        color: white;
                    }

                    &:active {
                        background-color: ${darken(
                            0.2,
                            palette.vibrant.raspberry
                        )} !important;
                        border-color: ${darken(
                            0.15,
                            palette.vibrant.raspberry
                        )} !important;
                        color: white;
                    }

                    &:focus {
                        box-shadow: 0 0 0 0.2rem
                            ${lighten(0.05, palette.vibrant.raspberry)} !important;
                        background-color: ${darken(
                            0.1,
                            palette.vibrant.raspberry
                        )};
                        color: white;
                    }
                `;
            case 'text':
            default:
                return css`
                    background: none !important;
                    border: none;
                    color: ${palette.graphite.heavyMetal};
                    font-weight: 100;
                    -webkit-transition: ease 0.2s all;
                    transition: ease 0.2s all;
                    width: none;
                    box-shadow: none !important;
                    &:hover {
                        color: ${primaryColor(props)};
                        background: none !important;
                        border: none;
                        text-decoration: underline;
                    }
                `;
        }
    }}

    ${(props) => {
        switch (props.size) {
            case 'tiny':
                return css`
                    padding: 7px 16px;
                    border-radius: 4px;
                    font-weight: 700;
                    font-size: 14px;
                `;
            case 'small':
                return css`
                    padding: 0.3rem 1.3rem;
                    line-height: 1.5;
                    border-radius: 0.2rem;
                    font-weight: 700;
                `;
            case 'fullWidth':
                return css`
                    width: 100%;
                `;
            default:
                return;
        }
    }}

    ${(props) =>
        props.disabled &&
        css`
            opacity: 0.3;
            pointer-events: none;
        `};
    ${(props) =>
        props.hidden &&
        css`
            opacity: 0;
        `};

    ${(props) =>
        props.height &&
        css`
            height: ${props.height}px;
        `};

    ${(props) =>
        props.$airy &&
        css`
            margin-left: 16px;
        `};

    ${(props) =>
        props.$noTextTransform &&
        css`
            text-transform: none;
        `};

    ${(props) =>
        props.$noWrap &&
        css`
            white-space: nowrap;
        `};
    ${(props) =>
        props.$center &&
        css`
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
        `};
    ${(props) =>
        props.flexGrow &&
        css`
            flex: ${props.flexGrow};
        `}
    ${(props) =>
        props.flexBasisPercent &&
        css`
            flex-basis: ${props.flexBasisPercent}%;
        `}
    ${(props) =>
        props.roundSize &&
        css`
            width: ${props.roundSize}px;
            height: ${props.roundSize}px;
            border-radius: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
        `};
`;

const StyledIcon = styled.span`
  & svg { font-size: 1.2rem;
  vertical-align: text-bottom;}
  /* Add any additional styles for icons here */
`;

export const ThemedButton = ({ text, $saving, style, icon, iconPosition, ...props }) => {
    const styles = {
        display: $saving ? 'inline-flex' : 'inline-block',
        padding: $saving ? '0.2rem 1rem 0.2rem 0rem': undefined,
        ...style
    }
    return (
        <StyledThemedButton {...props} style={styles}>
        {$saving ? <CircularProgress size={20} style={{color: palette.graphite.gray, margin: '8px'}}/> : null}
        {iconPosition === 'start' && <StyledIcon style={{ marginRight: '6px' }}>{icon}</StyledIcon>}
        {text}
        {iconPosition === 'end' && <StyledIcon style={{ marginLeft: '6px' }}>{icon}</StyledIcon>}
        </StyledThemedButton>
    );
};

ThemedButton.propTypes = {
    /** Adds left margin */
    $airy: PropTypes.bool,
    /** Button label. You can also pass another another element (e.g. string with carret element) */
    text: PropTypes.node,
    /** Centers button */
    $center: PropTypes.bool,
    /** Disables button. */
    disabled: PropTypes.bool,
    /** How much of the flex container's remaining space should be assigned to the flex item's main size.*/
    flexGrow: PropTypes.number,
    /** The flex-basis CSS property sets the initial main size of a flex item.
     * Value is calculated as a percentil.
     * Default value is auto.
     */
    flexBasisPercent: PropTypes.number,
    /** Set height of the button in px */
    height: PropTypes.number,
    /** Hide button */
    hidden: PropTypes.bool,
    /** Undo uppercase on button text */
    $noTextTransform: PropTypes.bool,
    /** Set white-space to nowrap => suppress line-breaks */
    $noWrap: PropTypes.bool,
    /** Set weight & height to identical provided number, with border-radius100% (rouded corners) */
    roundSize: PropTypes.number,
    /** Set button style, for default skip this prop in component */
    mode: PropTypes.oneOf([
        'primary',
        'secondary',
        'gray',
        'black',
        'red',
        'text',
    ]),
    /** Set button size, for default skip this prop in component*/
    size: PropTypes.oneOf(['tiny', 'small', 'fullWidth']),
    /** Optional click handler*/
    onClick: PropTypes.func,
    /** Show loading indicator */
    $saving: PropTypes.bool,
    /** Show button with icon */
    icon: PropTypes.node,
    /** Icon before(start) or after(end) the text */
    iconPosition: PropTypes.oneOf(['start', 'end'])
};

ThemedButton.defaultProps = {
    $airy: false,
    $center: false,
    disabled: false,
    hidden: false,
    $noTextTransform: false,
    $noWrap: false,
    onClick: undefined,
    flexGrow: 0,
    $saving: false,
    icon: null,
    iconPosition: null
};
